import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  Brand,
  Button,
  ButtonLink,
  LawWatchList,
  LawWatchTicker,
  marketingCopy,
} from '@aigovops/design-system';
import './landing.css';

/**
 * Public homepage `/` (refresh 2026-10-26).
 *
 * Built from the ux-adversary run `homepage-insurable-refresh` (3 rounds, 0 Blocker/High).
 * Every string comes from copy.ts (`gtmV10`, `lawWatch*`); every color is a DS token
 * (landing.css + direction-b.css). The page follows prefers-color-scheme.
 */

const { gtmV10: copy, lawWatch, lawWatchCopy, lawWatchVerifiedOn } = marketingCopy;

/** gacpro.io/demo: the Worker redirects to the booking page in DEMO_BOOKING_URL (routes/demo-redirect.ts). */
const DEMO_URL = '/demo';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Every "Request an assessment" is a native anchor to #pilot (Back works); focus then lands on the
 *  form heading, not an input, so no soft keyboard opens. */
function focusPilot() {
  window.setTimeout(() => document.getElementById('pilot-title')?.focus({ preventScroll: true }), 0);
}

function Arrow({ children }: { children: string }) {
  return <span aria-hidden="true">{children}</span>;
}

type FieldName = 'name' | 'email' | 'company';

function PilotSignupForm() {
  const f = copy.pilotForm;
  const [values, setValues] = useState({ name: '', email: '', company: '' });
  // Optional answers, sent as stored keys (owner / driver; empty = not answered). They also shape the success panel.
  const [owner, setOwner] = useState('');
  const [driver, setDriver] = useState('');
  const [invalid, setInvalid] = useState<Record<FieldName, boolean>>({ name: false, email: false, company: false });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [sentEmail, setSentEmail] = useState('');
  const refs = {
    name: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    company: useRef<HTMLInputElement>(null),
  };
  const successRef = useRef<HTMLDivElement>(null);
  const focusOnSuccess = useRef(false);

  const isValid = (k: FieldName, v: string) => (k === 'email' ? EMAIL_RX.test(v.trim()) : v.trim().length > 0);

  useEffect(() => {
    if (status === 'success' && focusOnSuccess.current) {
      focusOnSuccess.current = false;
      successRef.current?.focus();
    }
  }, [status]);

  const onChange = (k: FieldName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setValues((s) => ({ ...s, [k]: v }));
    // Re-check only a field that is already showing an error.
    if (invalid[k]) setInvalid((s) => ({ ...s, [k]: !isValid(k, v) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = {
      name: !isValid('name', values.name),
      email: !isValid('email', values.email),
      company: !isValid('company', values.company),
    };
    setInvalid(next);
    const first = (['name', 'email', 'company'] as const).find((k) => next[k]);
    if (first) {
      refs[first].current?.focus();
      return;
    }

    setStatus('submitting');
    setErrorMessage('');
    const { name, email, company } = values;
    const answers = { ...(owner ? { owner } : {}), ...(driver ? { driver } : {}) };
    try {
      const res = await fetch('/api/pilot-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, company, ...answers }),
      });
      if (!res.ok) {
        // Show the server's own message when it sends one (e.g. a validation message); otherwise plain copy.
        const data = await res.json().catch(() => ({} as { error?: string }));
        setStatus('error');
        setErrorMessage(typeof data.error === 'string' && data.error ? data.error : f.genericError);
        return;
      }
      setSentEmail(email.trim());
      focusOnSuccess.current = true;
      setStatus('success');
    } catch {
      // Network failure: never surface the browser's raw message ("Failed to fetch").
      setStatus('error');
      setErrorMessage(f.genericError);
    }
  };

  const field = (k: FieldName, label: string, err: string, extra: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="lp-field">
      <label htmlFor={`f-${k}`}>{label}</label>
      <input
        ref={refs[k]}
        id={`f-${k}`}
        name={k}
        value={values[k]}
        onChange={onChange(k)}
        required
        aria-invalid={invalid[k]}
        // Points at the error text only while an error is showing.
        aria-describedby={invalid[k] ? `f-${k}-err` : undefined}
        enterKeyHint="next"
        {...extra}
      />
      {invalid[k] ? <p className="lp-err" id={`f-${k}-err`}>{err}</p> : null}
    </div>
  );

  if (status === 'success') {
    const deadline = (f.deadlineDrivers as readonly string[]).includes(driver);
    return (
      // Focus moves here (the form is gone); no live region as well, so it is announced once.
      <div className="lp-success" ref={successRef} tabIndex={-1} aria-labelledby="pilot-success-title" data-pilot-success>
        <p className="lp-ok" id="pilot-success-title">{f.successTitle}</p>
        <p>{f.successBefore}<b>{sentEmail}</b>{f.successAfter}</p>
        <p className="lp-row">
          <button
            className="lp-linkbtn"
            type="button"
            onClick={() => {
              setStatus('idle');
              window.setTimeout(() => refs.email.current?.focus(), 0);
            }}
          >
            {f.edit}
          </button>
        </p>
        {deadline ? (
          <div className="lp-row">
            <span>{f.deadline}</span>
            <ButtonLink variant="secondary" compact className="lp-btn-sm" href={DEMO_URL} target="_blank" rel="noopener noreferrer">
              {copy.pilot.bookDemo}<span className="sr-only">{copy.pilot.newTab}</span>
            </ButtonLink>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={handleSubmit} noValidate aria-labelledby="pilot-title" aria-describedby="pilot-intro" data-pilot-form>
      <p className="lp-req">{f.required}</p>
      {field('name', f.name, f.nameError, { type: 'text', autoComplete: 'name', autoCapitalize: 'words' })}
      {field('email', f.email, f.emailError, { type: 'email', inputMode: 'email', autoComplete: 'email', autoCapitalize: 'none', spellCheck: false })}
      {field('company', f.company, f.companyError, { type: 'text', autoComplete: 'organization', autoCapitalize: 'words' })}
      <fieldset className="lp-field">
        <legend>{f.ownerLegend} <span className="lp-opt">{f.optional}</span></legend>
        <div className="lp-radios">
          {f.ownerOptions.map((o) => (
            <label key={o.value}>
              <input type="radio" name="owner" value={o.value} checked={owner === o.value} onChange={() => setOwner(o.value)} /> {o.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="lp-field">
        <label htmlFor="f-driver">{f.driverLabel} <span className="lp-opt">{f.optional}</span></label>
        <select id="f-driver" name="driver" value={driver} onChange={(e) => setDriver(e.target.value)}>
          <option value="">{f.driverPlaceholder}</option>
          {f.driverOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {status === 'error' ? (
        // The error always offers a working alternative next to it: book a time instead.
        <div className="lp-form-error" role="alert" data-pilot-error>
          <p className="lp-err">{errorMessage}</p>
          <ButtonLink variant="secondary" compact className="lp-btn-sm" href={DEMO_URL} target="_blank" rel="noopener noreferrer">
            {copy.pilot.bookDemo}<span className="sr-only">{copy.pilot.newTab}</span>
          </ButtonLink>
        </div>
      ) : null}
      <Button variant="primary" type="submit" className="lp-btn-md" disabled={status === 'submitting'}>
        {status === 'submitting' ? f.submitting : f.submit}
      </Button>
    </form>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const openedAt = useRef(0);

  useEffect(() => {
    if (!open) return;
    openedAt.current = window.scrollY;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); menuBtnRef.current?.focus(); }
    };
    // Close on a tap outside the header, and when the page itself scrolls (the menu scrolls on its own).
    const onDown = (e: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => { if (Math.abs(window.scrollY - openedAt.current) > 48) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  return (
    <header className="lp-header" ref={headerRef}>
      <div className="lp-wrap lp-header-row">
        <Link className="lp-brand" to="/" aria-label={`${copy.brandName} home`}>
          <span className="lp-brand-glyph" aria-hidden="true"><Brand variant="glyph" label="" /></span>
          {copy.brandName}
        </Link>
        <nav className="lp-nav" aria-label={copy.nav.primaryLabel}>
          <ul className="lp-navlinks">
            {copy.nav.links.map((l) => <li key={l.href}><a href={l.href}>{l.label}</a></li>)}
            <li><Link to="/signin">{copy.nav.signIn}</Link></li>
          </ul>
          <ButtonLink
            variant="secondary"
            compact
            className="lp-btn-sm"
            href="#pilot"
            onClick={focusPilot}
            aria-label={copy.cta}
            data-to-pilot
            data-header-cta
          >
            <span className="lp-cta-long">{copy.cta}</span>
            <span className="lp-cta-short">{copy.ctaShort}</span>
          </ButtonLink>
          <button
            ref={menuBtnRef}
            className="lp-menu-btn"
            type="button"
            aria-expanded={open}
            aria-controls="jump-menu"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? copy.nav.close : copy.nav.menu}
          </button>
        </nav>
      </div>
      <nav id="jump-menu" className="lp-jump" aria-label={copy.nav.jumpLabel} hidden={!open}>
        <ul>
          {copy.nav.menuLinks.map((l) => (
            <li key={l.href}><a href={l.href} onClick={() => setOpen(false)}>{l.label}</a></li>
          ))}
          <li><Link to="/signin" onClick={() => setOpen(false)}>{copy.nav.signIn}</Link></li>
        </ul>
      </nav>
    </header>
  );
}

function Ticks({ items }: { items: readonly string[] }) {
  return (
    <ul className="lp-ticks">
      {items.map((t) => <li key={t}><span className="lp-tick" aria-hidden="true">✓</span>{t}</li>)}
    </ul>
  );
}

interface PanelRow { readonly k: string; readonly s: string; readonly v: string; readonly chip: string; readonly tone: string }
function RowsPanel({ label, tag, ariaLabel, rows }: { label: string; tag: string; ariaLabel: string; rows: readonly PanelRow[] }) {
  return (
    <div className="lp-panel" role="img" aria-label={ariaLabel}>
      <div className="lp-panel-head"><span className="lp-label">{label}</span><span className="lp-tag">{tag}</span></div>
      <ul className="lp-rows">
        {rows.map((r) => (
          <li key={r.k}>
            <span className="lp-k">{r.k}<span className="lp-s">{r.s}</span></span>
            <span className="lp-v">{r.v}</span>
            <span className="lp-chip" data-tone={r.tone}>{r.chip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Landing() {
  // iOS Safari applies :active (the pressed state) only when a touchstart listener exists.
  useEffect(() => {
    const noop = () => {};
    document.addEventListener('touchstart', noop, { passive: true });
    return () => document.removeEventListener('touchstart', noop);
  }, []);

  // Arriving from another page at /#pilot (e.g. sign-in's "Request an assessment"): the SPA renders after the
  // browser's own anchor jump, so scroll to the form and focus its heading once mounted.
  useEffect(() => {
    if (window.location.hash !== '#pilot') return;
    document.getElementById('pilot')?.scrollIntoView?.();
    focusPilot();
  }, []);

  const card = copy.assessmentCard;

  return (
    <div className="lp">
      <a className="skip-link" href="#main">{copy.skipLink}</a>

      <SiteHeader />

      <LawWatchTicker entries={lawWatch} verifiedOn={lawWatchVerifiedOn} copy={lawWatchCopy} listId="law-watch" />

      <main id="main" tabIndex={-1}>
        {/* 1. Hero */}
        <section className="lp-hero" aria-labelledby="hero-title">
          <div className="lp-wrap lp-hero-grid">
            <div>
              <p className="lp-eyebrow lp-label"><span className="lp-eyebrow-dot" aria-hidden="true" />{copy.hero.eyebrow}</p>
              <h1 id="hero-title">
                <span>{copy.hero.h1Line1}</span> <span className="lp-h1-l2">{copy.hero.h1Line2}</span>
              </h1>
              <p className="lp-subhead">{copy.hero.subhead}</p>
              <div className="lp-ctas">
                <ButtonLink variant="primary" className="lp-btn-lg" href="#pilot" onClick={focusPilot} data-to-pilot data-hero-cta>
                  {copy.cta} <Arrow>→</Arrow>
                </ButtonLink>
                <ButtonLink variant="ghost" className="lp-btn-lg" href="#how">
                  {copy.hero.secondaryCta} <Arrow>↓</Arrow>
                </ButtonLink>
              </div>
              <p className="lp-micro"><strong>{copy.hero.microStrong}</strong> {copy.hero.micro}</p>
            </div>

            <figure className="lp-assess" aria-labelledby="assess-cap">
              <div className="lp-assess-head">
                <figcaption id="assess-cap" className="lp-label">
                  {card.caption}<span className="sr-only">{card.captionSr}</span>
                </figcaption>
                <span className="lp-pill">{card.pill}</span>
              </div>
              <div className="lp-assess-body">
                <div>
                  <p className="lp-sys-name">{card.systemName}</p>
                  <p className="lp-sys-meta">{card.systemMeta}</p>
                </div>
                <dl className="lp-kpis">
                  {card.kpis.map((k) => (
                    <div key={k.label}>
                      <dt className="lp-label">{k.label}</dt>
                      <dd data-tone={k.tone}><b>{k.value}</b>{k.sub}</dd>
                    </div>
                  ))}
                </dl>
                <div className="lp-path">
                  <p className="lp-label" id="path-label">{card.pathLabel}</p>
                  <ol aria-labelledby="path-label">
                    {card.path.map((p, i) => (
                      <li key={p.title} data-tone={p.tone}>
                        {i > 0 ? <span className="lp-arrow" aria-hidden="true">→</span> : null}
                        <b>{p.title}</b><span>{p.sub}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <p className="lp-agent">
                  <span className="lp-agent-glyph" aria-hidden="true">✳</span>
                  <span><b>{card.agentName}</b> {card.agentBody}</span>
                </p>
              </div>
            </figure>
          </div>
        </section>

        {/* 2. Four-step strip = how it works (target of "Explore the platform") */}
        <section className="lp-steps" id="how" aria-labelledby="how-title">
          <div className="lp-wrap">
            <div className="lp-steps-inner">
              <div className="lp-steps-head">
                <h2 id="how-title"><span>{copy.steps.headingLine1}</span> <span>{copy.steps.headingLine2}</span></h2>
                <p className="lp-steps-lede">{copy.steps.lede}</p>
              </div>
              <ol role="list">
                {copy.steps.items.map((s, i) => (
                  <li role="listitem" key={s.num}>
                    <span className="lp-step-n" aria-hidden="true">{s.num}</span>
                    <h3><span className="sr-only">{`${copy.steps.stepSrPrefix} ${i + 1}: `}</span>{s.title}</h3>
                    <p>{s.body}</p>
                  </li>
                ))}
              </ol>
              <a className="lp-next-cue" href="#problem" aria-label={copy.steps.nextCueLabel}><Arrow>↓</Arrow></a>
            </div>
          </div>
        </section>

        {/* 3. Problem + the full law-watch list */}
        <section className="lp-sec lp-problem" id="problem" aria-labelledby="problem-title">
          <div className="lp-wrap">
            <p className="lp-over lp-label">{copy.problem.overline}</p>
            <h2 id="problem-title">{copy.problem.headline}</h2>
            <p className="lp-body">{copy.problem.body}</p>
            <div className="lp-lawwatch">
              <LawWatchList
                id="law-watch"
                entries={lawWatch}
                verifiedOn={lawWatchVerifiedOn}
                copy={lawWatchCopy}
                cta={(
                  <ButtonLink variant="secondary" compact className="lp-btn-sm" href="#pilot" onClick={focusPilot} data-to-pilot>
                    {copy.cta} <Arrow>→</Arrow>
                  </ButtonLink>
                )}
              />
            </div>
          </div>
        </section>

        {/* 4. 02 Map */}
        <section className="lp-sec" id="map" aria-labelledby="map-title">
          <div className="lp-wrap lp-grid">
            <div>
              <h2 id="map-title"><span className="lp-h2-step">{copy.map.step}</span>{copy.map.heading}</h2>
              <p className="lp-body">{copy.map.body}</p>
              <ul className="lp-chips" aria-label={copy.map.frameworksLabel}>
                {copy.map.frameworks.map((fw) => <li key={fw}>{fw}</li>)}
              </ul>
              <Ticks items={copy.map.ticks} />
            </div>
            <div className="lp-panel-col">
              <RowsPanel {...copy.map.panel} />
            </div>
          </div>
        </section>

        {/* 5. 03 Measure (flipped) */}
        <section className="lp-sec" id="measure" aria-labelledby="measure-title">
          <div className="lp-wrap lp-grid" data-flip>
            <div>
              <h2 id="measure-title"><span className="lp-h2-step">{copy.measure.step}</span>{copy.measure.heading}</h2>
              <p className="lp-body">{copy.measure.body}</p>
              <Ticks items={copy.measure.ticks} />
            </div>
            <div className="lp-panel-col">
              <RowsPanel {...copy.measure.panel} />
              <p className="lp-panel-sources">
                <b>{copy.measure.sourcesLead}</b>
                {copy.measure.sources.map((s) => (
                  <span key={s}><span className="lp-sep" aria-hidden="true">{' · '}</span>{s}</span>
                ))}
              </p>
            </div>
          </div>
        </section>

        {/* 6. 04 Remediate */}
        <section className="lp-sec" id="remediate" aria-labelledby="remediate-title">
          <div className="lp-wrap lp-grid">
            <div>
              <h2 id="remediate-title"><span className="lp-h2-step">{copy.remediate.step}</span>{copy.remediate.heading}</h2>
              <p className="lp-body">{copy.remediate.body}</p>
              <Ticks items={copy.remediate.ticks} />
              <p className="lp-inline-cta">
                <ButtonLink variant="secondary" compact className="lp-btn-sm" href="#pilot" onClick={focusPilot} data-to-pilot>
                  {copy.cta} <Arrow>→</Arrow>
                </ButtonLink>
              </p>
            </div>
            <div className="lp-panel-col">
              <div className="lp-panel" role="img" aria-label={copy.remediate.panel.ariaLabel}>
                <div className="lp-panel-head">
                  <span className="lp-label">{copy.remediate.panel.label}</span>
                  <span className="lp-chip" data-tone="green">{copy.remediate.panel.signed}</span>
                </div>
                <ol className="lp-chain">
                  {copy.remediate.panel.chain.map((c) => <li key={c.k}><span>{c.k}</span><span>{c.v}</span></li>)}
                </ol>
                <div className="lp-panel-foot">
                  <span>{copy.remediate.panel.exportLabel}</span>
                  <ul>{copy.remediate.panel.exports.map((x) => <li key={x}>{x}</li>)}</ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. FedRAMP */}
        <section className="lp-sec" id="fedramp" aria-labelledby="fedramp-title">
          <div className="lp-wrap">
            <div className="lp-fed">
              <div>
                <p className="lp-over lp-label">{copy.fedramp.overline}</p>
                <h2 id="fedramp-title">{copy.fedramp.heading}</h2>
                <p className="lp-body">{copy.fedramp.body}</p>
              </div>
              <ol className="lp-fed-flow" aria-label={copy.fedramp.flowLabel}>
                {copy.fedramp.flow.map((s, i) => (
                  <li key={s}>{i > 0 ? <span className="lp-arrow" aria-hidden="true">→</span> : null}{s}</li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* 8. Request an assessment (target of every CTA) */}
        <section className="lp-sec" id="pilot" aria-labelledby="pilot-title">
          <div className="lp-wrap">
            <div className="lp-pilot-card">
              <div>
                <h2 id="pilot-title" tabIndex={-1}>{copy.pilot.heading}</h2>
                <p className="lp-body" id="pilot-intro">{copy.pilot.intro}</p>
                <p className="lp-trust-line">{copy.pilot.trustLine}</p>
                <div className="lp-pilot-alt">
                  <span>{copy.pilot.talkFirst}</span>
                  <ButtonLink variant="secondary" compact className="lp-btn-sm" href={DEMO_URL} target="_blank" rel="noopener noreferrer">
                    {copy.pilot.bookDemo}<span className="sr-only">{copy.pilot.newTab}</span>
                  </ButtonLink>
                </div>
              </div>
              <div>
                <PilotSignupForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap">
          <span>{copy.footer.copyright}</span>
          <nav aria-label={copy.footer.navLabel}>
            {copy.footer.links.map((l) => <Link key={l.href} to={l.href}>{l.label}</Link>)}
          </nav>
        </div>
      </footer>
    </div>
  );
}
